import React, { useState } from 'react';
import { Alert, Button, message } from 'antd';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { StripeCheckoutFormProps } from '../../interfaces';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { finalizeStripePaymentIntentOrder } from '../../../services';
import { getApiErrorMessage } from '../../../utils/apiError';

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  paymentIntentId,
  paymentIntentClientSecret,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const user = useAppSelector(state => state.user.user);
  const guestEmail = useAppSelector(state => state.shoppingCart.guestEmail);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);

  const finishOrder = async (succeededPaymentIntentId: string) => {
    await dispatch(finalizeStripePaymentIntentOrder(succeededPaymentIntentId)).unwrap();
    navigate('/checkout/success', {
      replace: true,
      state: {
        paymentConfirmed: true,
        paymentReferenceId: succeededPaymentIntentId,
        paymentMethod: 'Stripe',
        isAuthenticatedOrder: Boolean(user?.id),
      },
    });
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setPaymentError('Stripe form is not ready yet.');
      return;
    }

    if (!paymentElementComplete) {
      setPaymentError('Please enter complete payment details.');
      return;
    }

    setProcessing(true);
    setPaymentError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: user?.email || guestEmail || undefined,
              name: user?.name || undefined,
            },
          },
        },
      });

      if (error) {
        const retrievedPaymentIntent = await stripe.retrievePaymentIntent(paymentIntentClientSecret);

        if (retrievedPaymentIntent.paymentIntent?.status === 'succeeded') {
          await finishOrder(retrievedPaymentIntent.paymentIntent.id);
          return;
        }

        throw new Error(error.message || 'Payment failed');
      }

      const succeededPaymentIntentId = paymentIntent?.id || paymentIntentId;

      if (!succeededPaymentIntentId) {
        throw new Error('Stripe did not return payment intent id');
      }

      await finishOrder(succeededPaymentIntentId);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Payment failed. Please try again.');
      setPaymentError(errorMessage);
      message.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="payment-info-block">
      <div className="payment-info">
        <div className="payment-header">
          <div className="payment-copy">
            <h3 className="payment-title">Payment Details</h3>
            <p className="payment-subtitle">Secure card payment powered by Stripe.</p>
          </div>
        </div>
        <div className="stripe-field-group">
          <label>Card</label>
          <div className="stripe-element stripe-payment-element">
          <PaymentElement
            options={{ layout: 'accordion' }}
            onChange={(event) => {
              setPaymentElementComplete(event.complete);
              if (event.complete) {
                setPaymentError(null);
              }
            }}
          />
          </div>
        </div>

        {paymentError && <Alert type="error" showIcon message={paymentError} />}

        <Button
          className="button-pay"
          type="primary"
          onClick={handleSubmit}
          loading={processing}
          disabled={!stripe || !elements || processing || !paymentElementComplete}
          block
        >
          {processing ? 'Processing...' : 'Complete Payment'}
        </Button>
      </div>
    </div>
  );
};

export default StripeCheckoutForm;
